import { inject } from "@angular/core";
import {
  type HttpInterceptorFn,
  type HttpRequest,
  type HttpHandlerFn,
  type HttpEvent,
} from "@angular/common/http";
import { from, type Observable } from "rxjs";
import { mergeMap } from "rxjs/operators";

import { AUTH_PROVIDER } from "../auth/auth-provider.token";
import { environment } from "../../../environments/environment";

/**
 * Base URLs that must NOT receive the Authorization header (identity/graph).
 */
const EXCLUDED_URL_PREFIXES: readonly string[] = [
  "https://login.microsoftonline.com",
  "https://graph.microsoft.com",
  `https://${environment.auth0.domain}`,
] as const;

/** True when the request targets the app API and not an external IdP/graph. */
function shouldAttachToken(url: string): boolean {
  if (EXCLUDED_URL_PREFIXES.some((prefix: string) => url.startsWith(prefix))) {
    return false;
  }
  return url.startsWith(environment.apiBaseUrl);
}

/**
 * Acquires the SP-API access token via the active IAuthProvider (MSAL silent)
 * and attaches it as a Bearer header to outgoing API requests.
 */
export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  if (!shouldAttachToken(req.url)) {
    return next(req);
  }

  const provider = inject(AUTH_PROVIDER);

  return from(provider.acquireApiToken())
.pipe(
    mergeMap((token: string | null) => {
      if (!token) {
        return next(req);
      }
      return next(
        req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
      );
    })
  );
};
